import { FormEvent, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ExternalLink } from "@/components/ExternalLink";
import { siteLinks } from "@/config/branding";
import { horizonScanningCopy } from "@/config/horizonscanning/copy";
import { getPack } from "@/config/horizonscanning/packs";
import { topicLanguageLabel } from "@/config/horizonscanning/languages";
import {
  frequenciesUnlocked,
  normalizeDigestFrequency,
  RECIPIENT_CAP,
  SOURCE_MONTHLY_EUR,
} from "@/config/horizonscanning/options";
import { getSource, searchSources, publisherKey } from "@/config/horizonscanning/sources";
import { emptyWizardState, normalizeCustomTopic, type DigestFrequency } from "@/config/horizonscanning/types";
import { NextPhaseHandoff } from "@/components/NextPhaseHandoff";
import { withCurrentSearch } from "@/utils/searchParams";
import { getHorizonScanningApi, watchlistPublisherIds, type WatchlistResponse } from "./api";
import { getHorizonScanningSession, setHorizonScanningSession } from "./sessionKeys";
import { FrequencyStep } from "./steps/FrequencyStep";
import { TopicStep } from "./steps/TopicStep";

const isDevUi = import.meta.env.DEV;

export function WatchlistScreen() {
  const [params] = useSearchParams();
  const session = params.get("session") ?? getHorizonScanningSession("session") ?? "";
  const [data, setData] = useState<WatchlistResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [freqBusy, setFreqBusy] = useState(false);
  const [freqError, setFreqError] = useState<string | null>(null);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [editSources, setEditSources] = useState(false);
  const [draftSources, setDraftSources] = useState<string[]>([]);
  const [sourceBusy, setSourceBusy] = useState(false);
  const [sourceError, setSourceError] = useState<string | null>(null);
  const [sourceQuery, setSourceQuery] = useState("");
  const [editTopics, setEditTopics] = useState(false);
  const [topicBusy, setTopicBusy] = useState(false);
  const [topicError, setTopicError] = useState<string | null>(null);
  const [topicDraft, setTopicDraft] = useState(emptyWizardState());
  const [unsubBusy, setUnsubBusy] = useState(false);
  const [unsubError, setUnsubError] = useState<string | null>(null);

  useEffect(() => {
    if (!session) {
      setError("No session. Confirm the link in your inbox first.");
      return;
    }
    setHorizonScanningSession("session", session);

    const stripeSessionId = params.get("session_id");
    const run = async () => {
      try {
        if (stripeSessionId) {
          const paid = await getHorizonScanningApi().confirmStripeSession(stripeSessionId);
          setData(paid);
          return;
        }
        const wl = await getHorizonScanningApi().getWatchlist(session);
        setData(wl);
        if (params.get("checkout") === "1" && wl.checkoutEnabled && wl.showPaywall) {
          // Auto-start Checkout when arriving from day-7 email CTA.
          void startCheckout(session);
        }
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Could not load the watchlist.");
      }
    };
    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- checkout once on mount
  }, [session, params]);

  async function startCheckout(sessionToken: string) {
    setCheckoutBusy(true);
    setCheckoutError(null);
    try {
      const origin = window.location.origin;
      const successUrl = `${origin}/horizonscanning/watchlist?session=${encodeURIComponent(sessionToken)}&session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${origin}/horizonscanning/watchlist?session=${encodeURIComponent(sessionToken)}`;
      const { checkoutUrl } = await getHorizonScanningApi().createCheckoutSession(
        sessionToken,
        successUrl,
        cancelUrl,
      );
      window.location.assign(checkoutUrl);
    } catch (err: unknown) {
      setCheckoutError(err instanceof Error ? err.message : "Checkout is unavailable.");
      setCheckoutBusy(false);
    }
  }

  async function onInvite(e: FormEvent) {
    e.preventDefault();
    if (!session || !inviteEmail.trim()) return;
    setInviteBusy(true);
    setInviteError(null);
    try {
      const next = await getHorizonScanningApi().inviteRecipient(session, inviteEmail.trim());
      setData(next);
      setInviteEmail("");
    } catch (err: unknown) {
      setInviteError(err instanceof Error ? err.message : "Invite failed.");
    } finally {
      setInviteBusy(false);
    }
  }

  async function onRemove(email: string) {
    if (!session) return;
    try {
      const next = await getHorizonScanningApi().removeRecipient(session, email);
      setData(next);
    } catch (err: unknown) {
      setInviteError(err instanceof Error ? err.message : "Remove failed.");
    }
  }

  async function onMarkPaid() {
    if (!session) return;
    try {
      const next = await getHorizonScanningApi().markPaid(session);
      setData(next);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Could not mark paid.");
    }
  }

  async function onFrequencyChange(frequency: DigestFrequency) {
    if (!session || !data) return;
    if (normalizeDigestFrequency(data.frequency) === frequency) return;
    setFreqBusy(true);
    setFreqError(null);
    try {
      const next = await getHorizonScanningApi().updateFrequency(session, frequency);
      setData(next);
    } catch (err: unknown) {
      setFreqError(err instanceof Error ? err.message : "Could not update cadence.");
    } finally {
      setFreqBusy(false);
    }
  }

  function beginEditSources() {
    if (!data) return;
    setDraftSources([...watchlistPublisherIds(data)]);
    setEditSources(true);
    setSourceError(null);
  }

  async function saveSources() {
    if (!session) return;
    if (draftSources.length === 0) {
      setSourceError("Select at least one source.");
      return;
    }
    setSourceBusy(true);
    setSourceError(null);
    try {
      const next = await getHorizonScanningApi().updateWatchlist(session, { publisherIds: draftSources });
      setData(next);
      setEditSources(false);
    } catch (err: unknown) {
      setSourceError(err instanceof Error ? err.message : "Could not update sources.");
    } finally {
      setSourceBusy(false);
    }
  }

  function beginEditTopics() {
    if (!data) return;
    setTopicDraft({
      ...emptyWizardState(data.packId),
      packId: data.packId,
      packVersion: data.packVersion,
      topicIds: [...data.topicIds],
      includeAll: data.includeAll,
      customTopic: normalizeCustomTopic(data.customTopic),
    });
    setEditTopics(true);
    setTopicError(null);
  }

  async function saveTopics() {
    if (!session) return;
    if (!topicDraft.includeAll && topicDraft.topicIds.length === 0 && !topicDraft.customTopic) {
      setTopicError("Select a topic, add your own, or include all publications.");
      return;
    }
    setTopicBusy(true);
    setTopicError(null);
    try {
      const next = await getHorizonScanningApi().updateWatchlist(session, {
        packId: topicDraft.packId,
        packVersion: topicDraft.packVersion,
        topicIds: topicDraft.topicIds,
        includeAll: topicDraft.includeAll,
        customTopic: topicDraft.customTopic,
      });
      setData(next);
      setEditTopics(false);
    } catch (err: unknown) {
      setTopicError(err instanceof Error ? err.message : "Could not update topics.");
    } finally {
      setTopicBusy(false);
    }
  }

  async function onUnsubscribe() {
    if (!session) return;
    setUnsubBusy(true);
    setUnsubError(null);
    try {
      await getHorizonScanningApi().unsubscribe({ session });
      const next = await getHorizonScanningApi().getWatchlist(session);
      setData(next);
    } catch (err: unknown) {
      setUnsubError(err instanceof Error ? err.message : "Could not unsubscribe.");
    } finally {
      setUnsubBusy(false);
    }
  }

  async function onResumeDigests() {
    if (!session) return;
    setUnsubBusy(true);
    setUnsubError(null);
    try {
      await getHorizonScanningApi().unsubscribe({ session, resume: true });
      const next = await getHorizonScanningApi().getWatchlist(session);
      setData(next);
    } catch (err: unknown) {
      setUnsubError(err instanceof Error ? err.message : "Could not resume.");
    } finally {
      setUnsubBusy(false);
    }
  }

  if (error) {
    return (
      <div className="mx-auto max-w-xl px-5 py-16">
        <h1 className="text-2xl font-bold text-ink">Watchlist unavailable</h1>
        <p className="mt-3 text-ink-soft">{error}</p>
        <Link className="mt-6 inline-block font-semibold text-brand-700" to={withCurrentSearch("/horizonscanning/start")}>
          Start again
        </Link>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="mx-auto max-w-xl px-5 py-16">
        <p className="text-ink-soft">Loading your watchlist…</p>
      </div>
    );
  }

  const pack = getPack(data.packId);
  const customTopic = normalizeCustomTopic(data.customTopic);
  const advisor = data.persona === "advisor";
  const vendor = data.persona === "vendor";
  const sourceCount = watchlistPublisherIds(data).length;
  const monthlyEstimate = sourceCount * SOURCE_MONTHLY_EUR;
  const trialEnds = data.trialEndsAt ? new Date(data.trialEndsAt) : null;
  const showPaywall = data.showPaywall || data.planStatus === "expired";
  const checkoutEnabled = Boolean(data.checkoutEnabled);
  const paid = frequenciesUnlocked(data.planStatus);
  const cadenceFrequency = (() => {
    const f = normalizeDigestFrequency(data.frequency);
    return paid || f === "weekly" ? f : "weekly";
  })();
  const showColleagueNudge = (data.digestsSentCount ?? 0) >= 1 && data.recipients.length === 0;
  const catalogue = searchSources(sourceQuery);

  return (
    <div className="mx-auto w-full max-w-3xl px-5 py-12">
      <h1 className="text-3xl font-extrabold text-ink">Your watchlist</h1>
      <p className="mt-3 text-ink-soft">
        Admin: {data.adminEmail}. Complete coverage of the sources you select (not all regulation). Colleagues are
        recipients, not users. Cap: {RECIPIENT_CAP} recipients on the same domain.
      </p>
      <NextPhaseHandoff variant="horizon" />

      {data.digestsUnsubscribed ? (
        <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-ink">
          <p className="font-semibold">Digests are paused</p>
          <p className="mt-2 text-ink-soft">You unsubscribed this watchlist. Resume to start receiving matches again.</p>
          <button
            type="button"
            className="r1-btn-primary mt-3 inline-flex"
            disabled={unsubBusy}
            onClick={() => void onResumeDigests()}
          >
            {unsubBusy ? "Saving…" : "Resume digests"}
          </button>
          {unsubError ? <p className="mt-2 text-sm text-red-700">{unsubError}</p> : null}
        </div>
      ) : null}

      {showPaywall ? (
        <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-ink">
          <p className="font-semibold">Your free week has ended</p>
          <p className="mt-2 text-ink-soft">
            Digests are paused. Continue horizon scanning (€{SOURCE_MONTHLY_EUR} per source per month) and unlock daily,
            weekly or bi-weekly delivery.
          </p>
          <div className="mt-4 flex flex-wrap gap-3">
            {checkoutEnabled ? (
              <button
                type="button"
                className="r1-btn-primary inline-flex"
                disabled={checkoutBusy}
                onClick={() => void startCheckout(session)}
              >
                {checkoutBusy ? "Opening Checkout…" : "Continue with Checkout"}
              </button>
            ) : (
              <ExternalLink className="r1-btn-primary inline-flex" href={siteLinks.contact}>
                {horizonScanningCopy.contactUsLabel}
              </ExternalLink>
            )}
            {isDevUi ? (
              <button type="button" className="r1-btn-secondary inline-flex text-sm" onClick={onMarkPaid}>
                Dev: mark paid
              </button>
            ) : null}
          </div>
          {checkoutError ? <p className="mt-2 text-sm text-red-700">{checkoutError}</p> : null}
        </div>
      ) : data.planStatus === "trial" ? (
        <div className="mt-6 rounded-xl border border-brand-100 bg-brand-50 p-4 text-sm text-ink-soft">
          Free week in progress
          {trialEnds ? ` (ends ${trialEnds.toLocaleDateString()})` : ""}. Digests send weekly during the trial. After
          you continue, we honour your preferred frequency ({data.frequency}).
        </div>
      ) : null}

      {showColleagueNudge ? (
        <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-ink-soft">
          Your first digest is on its way. Invite a colleague on the same domain so they get the same newsletter.
        </div>
      ) : null}

      {advisor ? (
        <div className="mt-6 rounded-xl border border-brand-100 bg-brand-50 p-4 text-sm text-ink-soft">
          You marked yourself as an advisor. The digest is a teaser for client work. For a client portfolio,{" "}
          <ExternalLink href={siteLinks.contact} className="font-medium text-brand-700">
            contact us
          </ExternalLink>{" "}
          about API access and consultancy packaging.
        </div>
      ) : null}
      {vendor ? (
        <div className="mt-6 rounded-xl border border-brand-100 bg-brand-50 p-4 text-sm text-ink-soft">
          You marked yourself as a vendor. Embedding the feed in your product is an API conversation, not a digest
          upgrade.
        </div>
      ) : null}

      <section className="mt-8">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-bold">Pack and topics</h2>
          {!editTopics ? (
            <button type="button" className="text-sm font-semibold text-brand-700" onClick={beginEditTopics}>
              Edit topics
            </button>
          ) : null}
        </div>
        {!editTopics ? (
          <>
            <p className="text-ink-soft">{pack?.industry ?? data.packId}</p>
            <ul className="mt-2 list-disc pl-5 text-ink-soft">
              {data.includeAll ? <li>Include all publications (no topic filter)</li> : null}
              {data.topicIds.map((id) => {
                const topic = pack?.topics.find((t) => t.topicId === id);
                return (
                  <li key={id}>
                    {topic?.title ?? id}
                    {topic?.language ? ` (${topicLanguageLabel(topic.language)})` : ""}
                  </li>
                );
              })}
              {customTopic?.title ? (
                <li>
                  {customTopic.title} (your topic, {topicLanguageLabel(customTopic.language)})
                  {customTopic.terms.length ? (
                    <ul className="mt-1 list-disc pl-5 text-sm">
                      {customTopic.terms.map((t) => (
                        <li key={t.term}>
                          {t.term}
                          {t.proximity ? ` (proximity ${t.proximity})` : ""}
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              ) : null}
            </ul>
          </>
        ) : (
          <div className="mt-3 space-y-3">
            <TopicStep
              pack={getPack(topicDraft.packId)}
              state={topicDraft}
              resetSourcesOnPackChange={false}
              onChange={(patch) => setTopicDraft((prev) => ({ ...prev, ...patch }))}
            />
            <div className="flex flex-wrap gap-2">
              <button type="button" className="r1-btn-primary" disabled={topicBusy} onClick={() => void saveTopics()}>
                {topicBusy ? "Saving…" : "Save topics"}
              </button>
              <button type="button" className="r1-btn-secondary" disabled={topicBusy} onClick={() => setEditTopics(false)}>
                Cancel
              </button>
            </div>
            {topicError ? <p className="text-sm text-red-700">{topicError}</p> : null}
          </div>
        )}
      </section>

      <section className="mt-8">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-lg font-bold">Sources</h2>
          {!editSources ? (
            <button type="button" className="text-sm font-semibold text-brand-700" onClick={beginEditSources}>
              Edit sources
            </button>
          ) : null}
        </div>
        {!editSources ? (
          <ul className="mt-3 flex flex-wrap gap-3">
            {watchlistPublisherIds(data).map((id) => {
              const source = getSource(id);
              return (
                <li key={id} className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
                  {source ? <img src={source.logoPath} alt="" className="h-8 w-auto" /> : null}
                  <span className="text-sm font-medium">{source?.name ?? id}</span>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="mt-3 space-y-3">
            <input
              type="search"
              className="r1-field"
              placeholder="Search sources"
              value={sourceQuery}
              onChange={(e) => setSourceQuery(e.target.value)}
            />
            <div className="grid max-h-64 grid-cols-2 gap-2 overflow-y-auto sm:grid-cols-3">
              {catalogue.map((source) => {
                const id = publisherKey(source);
                const selected = draftSources.includes(id);
                return (
                  <button
                    key={id}
                    type="button"
                    className={`rounded-lg border px-2 py-2 text-left text-sm ${
                      selected ? "border-brand-500 bg-brand-50" : "border-slate-200 bg-white"
                    }`}
                    onClick={() =>
                      setDraftSources((prev) =>
                        selected ? prev.filter((x) => x !== id) : [...prev, id],
                      )
                    }
                  >
                    {source.shortName}
                  </button>
                );
              })}
            </div>
            <div className="flex flex-wrap gap-2">
              <button type="button" className="r1-btn-primary" disabled={sourceBusy} onClick={() => void saveSources()}>
                {sourceBusy ? "Saving…" : "Save sources"}
              </button>
              <button
                type="button"
                className="r1-btn-secondary"
                disabled={sourceBusy}
                onClick={() => setEditSources(false)}
              >
                Cancel
              </button>
            </div>
            {sourceError ? <p className="text-sm text-red-700">{sourceError}</p> : null}
          </div>
        )}
        {data.requestedSource ? (
          <p className="mt-2 text-sm text-ink-muted">Requested source: {data.requestedSource}</p>
        ) : null}
      </section>

      <section className="mt-8">
        <h2 className="text-lg font-bold">Cadence</h2>
        <p className="mt-1 text-sm text-ink-soft">
          Sending now as {data.effectiveFrequency}
          {data.planStatus === "trial" ? " (trial is weekly-only)" : ""}. Time{" "}
          {data.sendTime} ({data.timezone}).
        </p>
        <div className="mt-4">
          <FrequencyStep
            state={{
              ...emptyWizardState(data.packId),
              frequency: cadenceFrequency,
              sendTime: data.sendTime,
              timezone: data.timezone,
            }}
            planStatus={data.planStatus}
            showSchedule={false}
            onChange={(patch) => {
              if (patch.frequency) void onFrequencyChange(patch.frequency);
            }}
          />
        </div>
        {freqError ? <p className="mt-2 text-sm text-red-700">{freqError}</p> : null}
        {freqBusy ? <p className="mt-2 text-sm text-ink-muted">Saving cadence…</p> : null}
      </section>

      <section className="mt-8">
        <h2 className="text-lg font-bold">Colleagues</h2>
        <p className="mt-1 text-sm text-ink-soft">
          Same email domain as {data.adminEmail}. Each person confirms from their own inbox. Cap {RECIPIENT_CAP}{" "}
          (pending + confirmed).
        </p>
        <ul className="mt-3 space-y-2">
          {data.recipients.length === 0 ? (
            <li className="text-sm text-ink-muted">No colleagues invited yet.</li>
          ) : (
            data.recipients.map((r) => (
              <li
                key={r.email}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
              >
                <span>
                  {r.email}{" "}
                  <span className="text-ink-muted">({r.status})</span>
                </span>
                <button type="button" className="font-medium text-brand-700" onClick={() => onRemove(r.email)}>
                  Remove
                </button>
              </li>
            ))
          )}
        </ul>
        <form className="mt-4 flex flex-wrap gap-2" onSubmit={onInvite}>
          <input
            className="r1-field min-w-[14rem] flex-1"
            type="email"
            placeholder="colleague@your-domain.com"
            value={inviteEmail}
            onChange={(e) => setInviteEmail(e.target.value)}
            required
          />
          <button type="submit" className="r1-btn-primary" disabled={inviteBusy}>
            {inviteBusy ? "Sending…" : "Invite"}
          </button>
        </form>
        {inviteError ? <p className="mt-2 text-sm text-red-700">{inviteError}</p> : null}
      </section>

      <section className="mt-8 border-t border-slate-200 pt-8">
        <h2 className="text-lg font-bold">Subscription</h2>
        <p className="mt-2 text-ink-soft">{horizonScanningCopy.priceDetail}</p>

        {sourceCount > 0 ? (
          <p className="mt-3 text-ink">
            You selected {sourceCount} source{sourceCount === 1 ? "" : "s"}. Soft estimate ~€{monthlyEstimate}/month
            before tax (€{SOURCE_MONTHLY_EUR} × {sourceCount}).
          </p>
        ) : (
          <p className="mt-3 text-ink-soft">
            Pricing is ~€{SOURCE_MONTHLY_EUR} per source per month after the free week.
          </p>
        )}

        <div className="mt-5 flex flex-wrap gap-3">
          {checkoutEnabled && data.planStatus !== "paid" ? (
            <button
              type="button"
              className="r1-btn-primary inline-flex"
              disabled={checkoutBusy}
              onClick={() => void startCheckout(session)}
            >
              {checkoutBusy ? "Opening Checkout…" : "Continue with Checkout"}
            </button>
          ) : (
            <ExternalLink className="r1-btn-primary inline-flex" href={siteLinks.contact}>
              {horizonScanningCopy.contactUsLabel}
            </ExternalLink>
          )}
          {isDevUi ? (
            <button type="button" className="r1-btn-secondary inline-flex text-sm" onClick={onMarkPaid}>
              Dev: mark paid
            </button>
          ) : null}
        </div>
        {checkoutError ? <p className="mt-2 text-sm text-red-700">{checkoutError}</p> : null}
        <p className="mt-4 text-sm text-ink-muted">
          <Link className="font-medium text-brand-700" to={`/horizonscanning/feedback?org=${encodeURIComponent(data.orgId)}`}>
            Provide feedback
          </Link>
          {" · "}
          {data.digestsUnsubscribed ? (
            <button type="button" className="font-medium text-brand-700" disabled={unsubBusy} onClick={() => void onResumeDigests()}>
              Resume digests
            </button>
          ) : (
            <button type="button" className="font-medium text-brand-700" disabled={unsubBusy} onClick={() => void onUnsubscribe()}>
              {unsubBusy ? "Saving…" : "Unsubscribe"}
            </button>
          )}
        </p>
        {unsubError ? <p className="mt-2 text-sm text-red-700">{unsubError}</p> : null}
      </section>
    </div>
  );
}
